import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { productAPI } from '../services/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FiMinus, FiPlus, FiShield, FiTruck, FiRefreshCw, FiStar, FiCheck, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import './ProductPage.css';


// NAYA
const Stars = ({ rating, size = 16 }) => (
  <div className="stars" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
    {[1, 2, 3, 4, 5].map(s => (
      <FiStar
        key={s}
        size={size}
        style={{
          fill:   s <= rating ? '#f59e0b' : 'none',
          color:  s <= rating ? '#f59e0b' : '#d1d5db',
          flexShrink: 0,
        }}
      />
    ))}
  </div>
);


const ProductPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, buyNow } = useCart();
  const { user } = useAuth();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [activeTab, setActiveTab] = useState('description');
  const [adding, setAdding] = useState(false);
  const [review, setReview] = useState({ rating: 5, title: '', body: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);

  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const goToImg = useCallback((idx, total) => {
    setActiveImg(Math.max(0, Math.min(idx, total - 1)));
  }, []);

  const onImgTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const onImgTouchEnd = (e, total) => {
    if (touchStartX.current === null) return;
    const dx = touchStartX.current - e.changedTouches[0].clientX;
    const dy = Math.abs(touchStartY.current - e.changedTouches[0].clientY);
    if (Math.abs(dx) > 40 && Math.abs(dx) > dy) {
      if (dx > 0) {
        setActiveImg(i => Math.min(i + 1, total - 1));
      } else {
        setActiveImg(i => Math.max(i - 1, 0));
      }
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  useEffect(() => {
    setLoading(true);
    setActiveImg(0);
    productAPI.getOne(slug)
      .then(res => {
        setProduct(res.data);
        setSelectedVariant(res.data.variants?.[0]);
      })
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  useEffect(() => {
    if (!product) return;
    setRelatedLoading(true);
    productAPI.getAll({ category: product.category?.slug, limit: 4 })
      .then(res => {
        const all = res.data.results || res.data;
        setRelatedProducts(all.filter(p => p.slug !== slug).slice(0, 3));
      })
      .catch(() => {})
      .finally(() => setRelatedLoading(false));
  }, [product, slug]);

  // FIX: Build productInfo helper so price/name/label are always passed
  const getProductInfo = () => ({
    productName: product?.name || 'Selected Product',
    packLabel:   selectedVariant?.pack_label || '',
    price:       parseFloat(selectedVariant?.effective_price ?? selectedVariant?.discounted_price ?? selectedVariant?.price ?? 0),
  });

  const handleAddToCart = async () => {
    if (!selectedVariant) return;
    setAdding(true);
    // FIX: Pass productInfo so guest checkout shows correct name & price
    await addToCart(selectedVariant.id, quantity, getProductInfo());
    setAdding(false);
  };

  const handleBuyNow = async () => {
    if (!selectedVariant) return;
    setAdding(true);
    // FIX: Pass productInfo so guest checkout shows correct name & price
    await buyNow(selectedVariant.id, quantity, getProductInfo());
    setAdding(false);
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Please login to write a review'); return; }
    setSubmittingReview(true);
    try {
      await productAPI.addReview({ product: product.id, ...review });
      toast.success('Review submitted!');
      const res = await productAPI.getOne(slug);
      setProduct(res.data);
      setReview({ rating: 5, title: '', body: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) return (
    <div className="loading-container" style={{ minHeight: '60vh' }}>
      <div className="spinner" />
    </div>
  );

  if (!product) return null;

  const imgCount = product.images?.length || 0;

  return (
    <>
      <Helmet>
  <title>
    {product?.meta_title || product?.name || "Vedonica"}
  </title>

  <meta
    name="description"
    content={product.meta_description || product.short_description}
  />

  <meta
    property="og:title"
    content={product?.meta_title || product?.name}
  />

  <meta
    property="og:description"
    content={product.meta_description || product.short_description}
  />

  <meta property="og:type" content="product" />

  <meta
    property="og:url"
    content={`https://vedonica.in/product/${product.slug}`}
  />

  <meta
  property="og:image"
  content={
    product.images?.[0]?.image_url ||
    "https://vedonica.in/logo192.png"
  }
/>

  <meta
    name="theme-color"
    content="#0f5132"
  />

  <link
    rel="canonical"
    href={`https://vedonica.in/product/${product.slug}`}
  />
</Helmet>

      <div className="product-page page-wrapper">
        <div className="container">

          <div className="product-layout">

            <div className="product-gallery">
              <div
                className="product-gallery__main"
                onTouchStart={onImgTouchStart}
                onTouchEnd={(e) => onImgTouchEnd(e, imgCount)}
                style={{ position: 'relative', userSelect: 'none', overflow: 'hidden' }}
              >
                {product.images?.[activeImg]?.image_url ? (
                  <img
                    src={product.images[activeImg].image_url}
                    alt={product.name}
                    style={{ transition: 'opacity 0.2s ease' }}
                  />
                ) : (
                  <div className="product-gallery__placeholder">
                    <span></span>
                    <p>{product.name}</p>
                  </div>
                )}

                {imgCount > 1 && (
                  <>
                    <button
                      onClick={() => goToImg(activeImg - 1, imgCount)}
                      disabled={activeImg === 0}
                      style={{
                        position: 'absolute', left: 10, top: '50%',
                        transform: 'translateY(-50%)',
                        width: 34, height: 34, borderRadius: '50%',
                        background: 'rgba(255,255,255,0.9)',
                        border: '1px solid #c8e6c9',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', color: '#2e7d32',
                        opacity: activeImg === 0 ? 0.25 : 1,
                        transition: 'opacity 0.2s',
                        zIndex: 2, padding: 0,
                      }}
                    >
                      <FiChevronLeft size={18} />
                    </button>

                    <button
                      onClick={() => goToImg(activeImg + 1, imgCount)}
                      disabled={activeImg === imgCount - 1}
                      style={{
                        position: 'absolute', right: 10, top: '50%',
                        transform: 'translateY(-50%)',
                        width: 34, height: 34, borderRadius: '50%',
                        background: 'rgba(255,255,255,0.9)',
                        border: '1px solid #c8e6c9',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', color: '#2e7d32',
                        opacity: activeImg === imgCount - 1 ? 0.25 : 1,
                        transition: 'opacity 0.2s',
                        zIndex: 2, padding: 0,
                      }}
                    >
                      <FiChevronRight size={18} />
                    </button>

                    <div style={{
                      position: 'absolute', bottom: 12, left: '50%',
                      transform: 'translateX(-50%)',
                      display: 'flex', gap: 6, zIndex: 2,
                    }}>
                      {product.images.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => goToImg(i, imgCount)}
                          style={{
                            width: i === activeImg ? 22 : 7,
                            height: 7,
                            borderRadius: i === activeImg ? 4 : '50%',
                            background: i === activeImg ? '#2e7d32' : 'rgba(255,255,255,0.75)',
                            border: i === activeImg ? 'none' : '1px solid #a5d6a7',
                            padding: 0,
                            cursor: 'pointer',
                            transition: 'all 0.25s ease',
                          }}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {imgCount > 1 && (
                <div className="product-gallery__thumbs">
                  {product.images.map((img, i) => (
                    <button
                      key={img.id}
                      className={`product-gallery__thumb ${activeImg === i ? 'product-gallery__thumb--active' : ''}`}
                      onClick={() => goToImg(i, imgCount)}
                    >
                      <img src={img.image_url} alt={`${product.name} ${i + 1}`} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="product-details">
              <div className="product-details__header">
                <div className="section-tag" style={{ marginBottom: 12 }}>Ayurvedic Wellness</div>
                <h1 className="product-details__name">
  {selectedVariant?.name || product.name} – ₹
  {Number(selectedVariant?.effective_price || 0).toFixed(0)}
</h1>
                <div className="product-details__meta">
                  <Stars rating={Math.round(product.average_rating)} />
                  <span className="product-details__rating-num">{product.average_rating}</span>
                  <span className="product-details__review-count">({product.review_count} reviews)</span>
                </div>
                <p className="product-details__short-desc">
  {selectedVariant?.short_description || product.short_description}
</p>
              </div>

              <div className="product-variants">
                <p className="product-variants__label">Choose Your Pack</p>
                <div className="product-variants__options">
                  {product.variants?.map(v => (
                    <button
                      key={v.id}
                      className={`variant-card ${selectedVariant?.id === v.id ? 'variant-card--selected' : ''}`}
                      onClick={() => setSelectedVariant(v)}
                    >
                      {selectedVariant?.id === v.id && (
                        <span className="variant-card__check"><FiCheck size={12} /></span>
                      )}
                      <p className="variant-card__pack">{v.pack_label}</p>
                      <p className="variant-card__price">₹{Number(v.effective_price).toFixed(0)}</p>
                      {v.discount_percentage > 0 && (
                        <>
                          <p className="variant-card__original">₹{Number(v.price).toFixed(0)}</p>
                          <span className="variant-card__save">{v.discount_percentage}% OFF</span>
                        </>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {selectedVariant && (
                <div className="product-price">
                  <span className="price-new" style={{ fontSize: '2rem' }}>
                    ₹{Number(selectedVariant.effective_price).toFixed(2)}
                  </span>
                  {selectedVariant.discounted_price && (
                    <>
                      <span className="price-old" style={{ fontSize: '1.1rem' }}>
                        ₹{Number(selectedVariant.price).toFixed(2)}
                      </span>
                      <span className="price-save">Save {selectedVariant.discount_percentage}%</span>
                    </>
                  )}
                </div>
              )}

              <div className="product-qty">
                <p className="product-variants__label">Quantity</p>
                <div className="product-qty__control">
                  <button className="product-qty__btn" onClick={() => setQuantity(q => Math.max(1, q - 1))}>
                    <FiMinus />
                  </button>
                  <span className="product-qty__val">{quantity}</span>
                  <button className="product-qty__btn" onClick={() => setQuantity(q => Math.min(selectedVariant?.stock || 10, q + 1))}>
                    <FiPlus />
                  </button>
                </div>
                {selectedVariant && (
                  <p className="product-qty__stock">{selectedVariant.stock} in stock</p>
                )}
              </div>

              <div className="product-actions">
                <button
                  className="btn btn-primary product-actions__add"
                  onClick={handleAddToCart}
                  disabled={adding || !selectedVariant}
                >
                  {adding ? 'Adding...' : 'Add to Cart'}
                </button>
                <button
                  className="btn btn-outline product-actions__buy"
                  onClick={handleBuyNow}
                  disabled={adding || !selectedVariant}
                >
                  Buy Now
                </button>
              </div>

              <div className="product-trust">
                <div className="product-trust__item">
                  <FiTruck size={18} /> <span>Free shipping on orders ₹500+</span>
                </div>
                <div className="product-trust__item">
                  <FiShield size={18} /> <span>100% authentic & natural</span>
                </div>
                <div className="product-trust__item">
                  <FiRefreshCw size={18} /> <span>30-day hassle-free returns</span>
                </div>
              </div>
            </div>
          </div>

          <div className="product-tabs">
            <div className="product-tabs__nav">
              {['description', 'benefits', 'how-to-use', 'reviews'].map(tab => (
                <button
                  key={tab}
                  className={`product-tabs__tab ${activeTab === tab ? 'product-tabs__tab--active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === 'how-to-use' ? 'How to Use' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                  {tab === 'reviews' && ` (${product.review_count})`}
                </button>
              ))}
            </div>

            <div className="product-tabs__content">
              {activeTab === 'description' && (
                <div className="tab-description">
                  <p>{product.description}</p>
                  {product.ingredients && (
                    <>
                      <h4>Key Ingredients</h4>
                      <p>{product.ingredients}</p>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'benefits' && (
                <div className="tab-benefits">
                  {product.benefits?.map((b, i) => (
                    <div key={i} className="tab-benefit-item">
                      <span className="tab-benefit-icon"><FiCheck size={14} /></span>
                      <span>{b}</span>
                    </div>
                  ))}
                </div>
              )}

             
{activeTab === 'how-to-use' && (
  <div className="tab-how-to-use">
    {product.how_to_use_steps?.length > 0 ? (
    
<div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, justifyContent: 'center' }}>
  {product.how_to_use_steps.map((step, i) => (
    <div key={step.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, flex: '1 1 30%', maxWidth: '32%' }}>
            {step.image_url && (
              <img
                src={step.image_url}
                alt={`Step ${i + 1}`}
                style={{ width: '100%', borderRadius: 12, display: 'block', objectFit: 'contain', maxHeight: 300 }}
              />
            )}
            {step.step_text && (
              <p style={{ fontSize: 14, color: '#3a5244', textAlign: 'center', margin: 0, lineHeight: 1.6 }}>
                <strong style={{ color: '#1a5c3a' }}> {i + 1}:</strong> {step.step_text}
              </p>
            )}
          </div>
        ))}
      </div>
    ) : (
      product.how_to_use?.split('\n').filter(Boolean).map((step, i) => (
        <div key={i} className="tab-step">
          <span className="tab-step__num">{i + 1}</span>
          <span>{step.replace(/^\d+\.\s*/, '')}</span>
        </div>
      ))
    )}
  </div>
)}

              {activeTab === 'reviews' && (
                <div className="tab-reviews">
                  <div className="review-form">
                    <h3 className="review-form__title">Write a Review</h3>
                    <form onSubmit={handleReviewSubmit}>
                      <div className="review-form__rating">
                        <label className="form-label">Rating</label>
                        <div className="review-stars">
                          {[1,2,3,4,5].map(s => (
                            <button
                              key={s}
                              type="button"
                              className={`review-star-btn ${s <= review.rating ? 'review-star-btn--active' : ''}`}
                              onClick={() => setReview(r => ({ ...r, rating: s }))}
                            >
                              <FiStar size={22} />
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Title</label>
                        <input
                          className="form-input"
                          value={review.title}
                          onChange={e => setReview(r => ({ ...r, title: e.target.value }))}
                          placeholder="Summarize your experience"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Your Review</label>
                        <textarea
                          className="form-input"
                          rows={4}
                          value={review.body}
                          onChange={e => setReview(r => ({ ...r, body: e.target.value }))}
                          placeholder="Tell us about your experience..."
                          required
                        />
                      </div>
                      <button className="btn btn-primary" type="submit" disabled={submittingReview}>
                        {submittingReview ? 'Submitting...' : 'Submit Review'}
                      </button>
                    </form>
                  </div>

                  <div className="reviews-list">
                    {product.reviews?.length === 0 ? (
                      <p className="reviews-empty">No reviews yet. Be the first!</p>
                    ) : (
                      product.reviews.map(r => (
                        <div key={r.id} className="review-card">
                          <div className="review-card__header">
                            <Stars rating={r.rating} size={14} />
                            {r.is_verified && (
                              <span className="badge badge-green" style={{ fontSize: '0.7rem' }}>
                                Verified
                              </span>
                            )}
                          </div>
                          <h4 className="review-card__title">{r.title}</h4>
                          <p className="review-card__body">{r.body}</p>
                          <p className="review-card__author">
                            — {r.user_name} · {new Date(r.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {(relatedLoading || relatedProducts.length > 0) && (
            <section className="related-products">
              <div className="related-products__header">
                <div className="section-tag">You May Also Like</div>
                <h2 className="related-products__title">Related Products</h2>
              </div>

              {relatedLoading ? (
                <div className="related-products__loading">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="related-card related-card--skeleton">
                      <div className="related-card__img skeleton-box" />
                      <div className="related-card__body">
                        <div className="skeleton-line skeleton-line--lg" />
                        <div className="skeleton-line skeleton-line--sm" />
                        <div className="skeleton-line skeleton-line--md" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="related-products__grid">
                  {relatedProducts.map(rp => {
                    const variant = rp.variants?.[0];
                    return (
                      <div
                        key={rp.id}
                        className="related-card"
                        onClick={() => navigate(`/product/${rp.slug}`)}
                      >
                        <div className="related-card__img">
                          {rp.images?.[0]?.image_url ? (
                            <img src={rp.images[0].image_url} alt={rp.name} />
                          ) : (
                            <div className="related-card__placeholder"></div>
                          )}
                          {variant?.discount_percentage > 0 && (
                            <span className="related-card__badge">
                              {variant.discount_percentage}% OFF
                            </span>
                          )}
                        </div>

                        <div className="related-card__body">
                          <p className="related-card__name">{rp.name}</p>
                          <div className="related-card__meta">
                            <Stars rating={Math.round(rp.average_rating)} size={12} />
                            <span className="related-card__rating">{rp.average_rating}</span>
                          </div>
                          <div className="related-card__price">
                            <span className="price-new">
                              ₹{Number(variant?.effective_price || 0).toFixed(0)}
                            </span>
                            {variant?.discount_percentage > 0 && (
                              <span className="price-old">
                                ₹{Number(variant?.price || 0).toFixed(0)}
                              </span>
                            )}
                          </div>
                          <button
                            className="btn btn-primary related-card__btn"
                            onClick={e => {
                              e.stopPropagation();
                              // FIX: Pass productInfo for related product add-to-cart too
                              addToCart(variant?.id, 1, {
                                productName: rp.name,
                                packLabel:   variant?.pack_label || '',
                                price:       parseFloat(variant?.effective_price ?? variant?.price ?? 0),
                              });
                            }}
                            disabled={!variant}
                          >
                            Add to Cart
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}

        </div>
      </div>
    </>
  );
};

export default ProductPage;